import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from './components/CheckoutForm';
import DonationForm from './components/DonationForm';
import CookieBanner from './components/CookieBanner';
import logoImage from './assets/images/logo_cemcat.png';
import { api } from './services/api';
import './App.css';
import './components/Footer.css';

// Get the publishable key from environment variables
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const App: React.FC = () => {
  const [clientSecret, setClientSecret] = useState<string>('');
  const [showPaymentElement, setShowPaymentElement] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const [formData, setFormData] = useState({
    donorName: '',
    donorSurname: '',
    donorEmail: '',
    donorMessage: '',
    recipientDesignation: '',
    recipientSurname: '',
    recipientEmail: '',
    giftType: 'per-mi',
    donationAmount: '5'
  });

  const handleFormChange = (newData: Partial<typeof formData>) => {
    setFormData({ ...formData, ...newData });
    // Clear server error when form data changes
    if (serverError) {
      setServerError(undefined);
    }
  };

  const handleProceedToPayment = async () => {
    // Clear any previous errors
    setServerError(undefined);
    
    // Create PaymentIntent when user proceeds to payment
    try {
      const data = await api.post('/create-payment-intent', { 
        amount: formData.donationAmount ? parseInt(formData.donationAmount) : 5,
        currency: 'eur',
        email: formData.donorEmail,
        // Send all form data
        donorName: formData.donorName,
        donorSurname: formData.donorSurname,
        donorMessage: formData.donorMessage,
        recipientDesignation: formData.recipientDesignation,
        recipientSurname: formData.recipientSurname,
        recipientEmail: formData.recipientEmail,
        giftType: formData.giftType
      });
      
      if (data.error) {
        // Handle server-side error
        if (data.error.includes('Metadata values can have up to 500 characters')) {
          setServerError('El missatge personalitzat no pot superar els 500 caràcters.');
        } else {
          setServerError(data.error);
        }
        return;
      }
      
      setClientSecret(data.clientSecret);
      setShowPaymentElement(true);
      
      // Scroll to the payment form after it renders
      setTimeout(() => {
        const progressElement = document.querySelector('.payment-progress');
        window.scrollTo({
          top: progressElement ? (progressElement as HTMLElement).offsetTop : 0,
          behavior: 'smooth'
        });
      }, 100);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      setServerError('Hi ha hagut un error en processar la sol·licitud. Si us plau, intenta-ho de nou més tard.');
    }
  };

  const handleGoBack = () => {
    setShowPaymentElement(false);
  };

  return (
    <div className="App">
      <div className="donation-container">
        <div className="header-container">
          <img src={logoImage} alt="CEMCAT Logo" className="cemcat-logo" />
          <nav className="header-nav">
            <a href="#com-funciona" className="header-nav-link">Com funciona?</a>
          </nav>
        </div>
        
        <div className="main-title">
          <h1>Regala una Rosa Digital.<br />Regala Futur.</h1>
          <p className="donation-subheader">El teu donatiu dona suport a la recerca de l'esclerosi múltiple al CEMCAT.</p>
        </div>
        
        {!showPaymentElement ? (
          <div className="form-content">
            <div className="form-left">
              <DonationForm 
                formData={formData}
                onFormChange={handleFormChange}
                onSubmit={handleProceedToPayment}
                serverError={serverError}
              />
            </div>
          </div>
        ) : clientSecret && (
          <>
            <div className="payment-progress">
              <div className="progress-step completed">
                <div className="step-number">1</div>
                <div className="step-label">Dades</div>
              </div>
              <div className="progress-line"></div>
              <div className="progress-step active">
                <div className="step-number">2</div>
                <div className="step-label">Pagament</div>
              </div>
            </div>
            <div className="form-content">
              <div className="form-left">
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <CheckoutForm formData={formData} onGoBack={handleGoBack} />
                </Elements>
              </div>
            </div>
          </>
        )}
        <div className="footer">
          <p>Gràcies per donar suport a la recerca d'EM al CEMCAT.</p>
        </div>

        <section id="com-funciona" className="how-it-works">
          <h2>Com funciona?</h2>
          <p className="how-it-works-subtitle">Descobreix com és la Rosa Digital que rebrà el teu destinatari.</p>
          <div className="how-it-works-video">
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              src="/rosaexplanation.mp4"
            >
              El teu navegador no suporta la reproducció de vídeo.
            </video>
          </div>
        </section>

        <footer className="site-footer">
          <div
            style={{
              backgroundColor: "#007b8f",
              padding: "40px 20px",
              color: "white",
              fontFamily: "Arial, sans-serif",
              textAlign: "center"
            }}
          >
            <h2 style={{ marginTop: 0 }}>
              L’Art Teràpia al Cemcat: creativitat i expressió per Sant Jordi
            </h2>
            <p
              style={{
                maxWidth: "700px",
                margin: "0 auto 30px"
              }}
            >
              Cada any, en el marc del programa d’
              <strong>Art Teràpia al Cemcat</strong>, els nostres pacients participen en una activitat molt especial: la creació de la seva pròpia
              <strong> Rosa de Sant Jordi</strong>. Aquesta iniciativa, més enllà de la vessant artística, esdevé un espai d’expressió emocional, connexió i creativitat compartida.
            </p>
            <p
              style={{
                maxWidth: "700px",
                margin: "0 auto 40px"
              }}
            >
              A continuació compartim algunes imatges d’aquestes creacions úniques.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "20px",
                flexWrap: "wrap"
              }}
            >
              <img
                src="/rosa1.jpeg"
                alt="Rosa Sant Jordi 1"
                style={{
                  borderRadius: "8px",
                  width: "30%",
                  maxWidth: "300px",
                  height: "auto"
                }}
              />
              <img
                src="/rosa2.jpeg"
                alt="Rosa Sant Jordi 2"
                style={{
                  borderRadius: "8px",
                  width: "30%",
                  maxWidth: "300px",
                  height: "auto"
                }}
              />
              <img
                src="/rosa3.jpeg"
                alt="Rosa Sant Jordi 3"
                style={{
                  borderRadius: "8px",
                  width: "30%",
                  maxWidth: "300px",
                  height: "auto"
                }}
              />
              <img
                src="/rosa4.jpeg"
                alt="Rosa Sant Jordi 4"
                style={{
                  borderRadius: "8px",
                  width: "30%",
                  maxWidth: "300px",
                  height: "auto"
                }}
              />
            </div>
          </div>
          <div className="footer-top">
            <p>La recerca i la docència són pilars bàsics del Centre d'Esclerosi Múltiple de Catalunya (Cemcat) i milloren en l'atenció que es dóna al pacient.</p>
          </div>
          
          <div className="footer-middle">
            <div className="footer-logo">
              <img src={logoImage} alt="CEMCAT Logo" />
            </div>
            
            <div className="footer-contact">
              <h3>Estem en contacte</h3>
              <div className="contact-details">
                <div className="contact-item">
                  <span className="icon">📞</span>
                  <span>+34 93 175 15 59</span>
                </div>
                <div className="contact-item">
                  <span className="icon">✉️</span>
                  <span>fundacio.cemcat@cem-cat.org</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <div className="legal-links">
              <a href="https://www.cem-cat.org/ca/pol%C3%ADtica-de-privacitat" target="_blank" rel="noopener noreferrer">Política de privacitat</a>
              <span className="separator">·</span>
              <a href="https://www.cem-cat.org/ca/av%C3%ADs-legal" target="_blank" rel="noopener noreferrer">Avís legal</a>
              <span className="separator">·</span>
              <a href="https://www.cem-cat.org/ca/pol%C3%ADtica-de-cookies" target="_blank" rel="noopener noreferrer">Política de cookies</a>
              <span className="separator">·</span>
              <a href="https://www.cem-cat.org/ca/condicions-generals-de-contractaci%C3%B3" target="_blank" rel="noopener noreferrer">Condicions generals de contractació</a>
              <span className="separator">·</span>
              <a href="https://www.cem-cat.org/ca/pol%C3%ADtica-de-privacitat-xarxes-socials" target="_blank" rel="noopener noreferrer">Política de xarxes socials</a>
            </div>
            <div className="copyright">
              © {new Date().getFullYear()} Centre d'Esclerosi Múltiple de Catalunya
            </div>
          </div>
        </footer>
      </div>
      <CookieBanner />
    </div>
  );
};

export default App; 
